import React, { forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import GlassCard from '../shared/GlassCard';
import BrandMark from '../shared/BrandMark';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';
import { cardEntrance } from './animations';

/**
 * AuthCard
 * Branded form shell. Wraps GlassCard with BrandMark, title, subtitle, and animated entrance.
 * Exposes pulse() via ref for success feedback (boxShadow glow + scale).
 *
 * @param {string} title
 * @param {string} [subtitle]
 * @param {React.ReactNode} children
 * @param {number} [maxWidth=440]
 * @param {object} [sx]
 */
const AuthCard = forwardRef(function AuthCard(
  { title, subtitle, children, maxWidth = 440, sx = {} },
  ref,
) {
  const theme = useTheme();
  const pulseControls = useAnimation();
  const reduced = useReducedMotionPreferred();

  const mdShadow = theme.shadowsExt?.md ?? '0 8px 20px rgba(15,23,42,0.08)';
  const glowPrimary = theme.shadowsExt?.glowPrimary ?? '0 8px 28px rgba(242,101,34,0.28)';

  useImperativeHandle(ref, () => ({
    pulse() {
      if (reduced) {
        // Reduced-motion: shadow flash only, no scale.
        pulseControls.start({
          boxShadow: [mdShadow, glowPrimary, mdShadow],
          transition: { duration: 0.6, ease: [0.3, 0, 0, 1] },
        });
      } else {
        pulseControls.start({
          boxShadow: [mdShadow, glowPrimary, mdShadow],
          scale: [1, 1.012, 1],
          transition: { duration: 0.6, ease: [0.3, 0, 0, 1] },
        });
      }
    },
  }));

  // Entrance: opacity-only under reduced motion; soft spring with y+scale otherwise.
  const initialProps = reduced ? { opacity: 0 } : cardEntrance.initial;
  const animateProps = reduced
    ? { opacity: 1, transition: { duration: 0.24 } }
    : cardEntrance.animate;

  return (
    // Outer: entrance animation
    <motion.div
      initial={initialProps}
      animate={animateProps}
      style={{ width: '100%', maxWidth }}
    >
      {/* Inner: pulse controls — isolated so entrance and pulse don't conflict */}
      <motion.div
        animate={pulseControls}
        style={{ width: '100%', borderRadius: theme.radius?.lg ?? 16 }}
      >
        <GlassCard
          blur={16}
          sx={{
            px: { xs: 3, sm: 4 },
            py: { xs: 3, sm: 4 },
            borderRadius: theme.radius?.lg ?? 16,
            width: '100%',
            boxShadow: mdShadow,
            ...sx,
          }}
        >
          {/* Brand mark */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
            <BrandMark size={48} variant="full" />
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            component="h1"
            fontWeight={700}
            textAlign="center"
            sx={{ mb: subtitle ? 0.75 : 2.5, color: 'text.primary' }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          {subtitle && (
            <Typography
              variant="body2"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              {subtitle}
            </Typography>
          )}

          {children}
        </GlassCard>
      </motion.div>
    </motion.div>
  );
});

export default AuthCard;
